import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import format from 'date-fns/format';

import ContentItem from 'components/common/InfoPopup/ContentItem';
import ContentSection from 'components/common/InfoPopup/ContentSection';
import { getCurrency } from 'helpers/currency';
import { FORMAT_DATE } from 'constants/time';
import { is24hoursTimeGap } from 'helpers/date';

import styles from './styles.module.scss';

function getTimeFormatted(from, to) {
  const pad = (num) => String(num).padStart(2, '0');

  return `${pad(from.getHours())}:${pad(from.getMinutes())} - ${pad(to.getHours())}:${pad(to.getMinutes())}`;
}

export default function FreightsFiltersData({ filtersData, isOwner = false }) {
  const { t } = useTranslation();
  const getTranslate = (str) => {
    return t('exchangeModal.learn_more_section.' + str);
  };

  const loadingFrom = filtersData.loading_from ? new Date(filtersData.loading_from) : undefined;
  const loadingTo = filtersData.loading_to ? new Date(filtersData.loading_to) : undefined;
  const unloadingFrom = filtersData.unloading_from ? new Date(filtersData.unloading_from) : undefined;
  const unloadingTo = filtersData.unloading_to ? new Date(filtersData.unloading_to) : undefined;
  const gpsTracking = filtersData.gps_tracking ?? false;
  const showGpsLink = isOwner && filtersData.isActiveProposal && filtersData.gps_tracking_link;

  return (
    <>
      {showGpsLink && (
        <ContentSection title={t('cargoTracking.title')}>
          <ContentItem
            value={
              <a
                href={filtersData.gps_tracking_link}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.gpsLink}
              >
                {filtersData.gps_tracking_link}
              </a>
            }
            isFullWidth
          />
        </ContentSection>
      )}
      <ContentSection title={getTranslate('date_section')} withBorder={showGpsLink}>
        <ContentItem
          title={t('cargoFields.date_time_section.date_uploading')}
          value={
            loadingFrom && loadingTo
              ? `${format(loadingFrom, FORMAT_DATE)} - ${format(loadingTo, FORMAT_DATE)}`
              : format(loadingFrom, FORMAT_DATE)
          }
        />
        {loadingFrom && loadingTo && (
          <ContentItem
            title={t('cargoFields.date_time_section.time_uploading')}
            value={
              is24hoursTimeGap(loadingFrom, loadingTo)
                ? t('cargoFields.date_time_section.24_box')
                : getTimeFormatted(loadingFrom, loadingTo)
            }
          />
        )}
        <ContentItem
          title={t('cargoFields.date_time_section.date_unloading')}
          value={
            unloadingFrom && unloadingTo
              ? `${format(unloadingFrom, FORMAT_DATE)} - ${format(unloadingTo, FORMAT_DATE)}`
              : format(unloadingTo, FORMAT_DATE)
          }
        />
        {unloadingFrom && unloadingTo && (
          <ContentItem
            title={t('cargoFields.date_time_section.time_unloading')}
            value={
              is24hoursTimeGap(unloadingFrom, unloadingTo)
                ? t('cargoFields.date_time_section.24_box')
                : getTimeFormatted(unloadingFrom, unloadingTo)
            }
          />
        )}
      </ContentSection>
      <ContentSection title={getTranslate('itinerary_section')} withBorder>
        <li className={styles.row}>
          <ul className={styles.container}>
            <ContentItem
              title={t('cargoFields.itinerary_section.uploading')}
              value={`${filtersData.loading_address.full_address} ${filtersData.loading_address.country_code}`}
            />
            {filtersData.additional_loading_addresses.map((item, index) => (
              <ContentItem
                key={index}
                title={t('cargoFields.itinerary_section.additonal_uploading')}
                value={item.full_address}
              />
            ))}
          </ul>
        </li>
        <li className={styles.row}>
          <ul className={styles.container}>
            <ContentItem
              title={t('cargoFields.itinerary_section.unloading')}
              value={`${filtersData.unloading_address.full_address} ${filtersData.unloading_address.country_code}`}
            />
            {filtersData.additional_unloading_addresses.map((item, index) => (
              <ContentItem
                key={index}
                title={t('cargoFields.itinerary_section.additonal_unloading')}
                value={item.full_address}
              />
            ))}
          </ul>
        </li>
        {filtersData.round_trip && <ContentItem value={t('cargoFields.itinerary_section.round_trip_box')} />}
      </ContentSection>
      <ContentSection title={getTranslate('cargo_section')} withBorder>
        <ContentItem
          title={t('cargoFields.cargo_specification.cargo_name')}
          value={filtersData.cargo_designation || filtersData.cargo_designations_additional || ''}
        />
        {(filtersData.adr.length > 0 || filtersData.dopAdr.length > 0) && (
          <>
            {filtersData.adr.length > 0 && (
              <ContentItem
                title={t('cargoFields.cargo_specification.adr')}
                value={filtersData.adr.map((el) => `- ${el.title}`)}
              />
            )}
            {filtersData.dopAdr.length > 0 && (
              <ContentItem
                title={t('cargoFields.cargo_specification.add_adr')}
                value={filtersData.dopAdr.map((el) => `- ${el.title}`)}
              />
            )}
          </>
        )}
        {filtersData.weight > 0 && (
          <ContentItem
            title={t('cargoFields.cargo_specification.weight2')}
            value={`${filtersData.weight} ${t('units.tons')}`}
          />
        )}
        {filtersData.package && (
          <ContentItem title={t('cargoFields.cargo_specification.package')} value={filtersData.package} />
        )}
        {filtersData.belts && (
          <ContentItem title={t('cargoFields.cargo_specification.straps')} value={filtersData.belts} />
        )}
        {filtersData.volume > 0 && (
          <ContentItem
            title={t('cargoFields.cargo_specification.cubage')}
            value={
              <>
                {filtersData.volume} {t('units.cubage')}
                <sup>3</sup>
              </>
            }
          />
        )}
        {filtersData.length > 0 && (
          <ContentItem
            title={t('cargoFields.cargo_specification.length2')}
            value={`${filtersData.length} ${t('units.cubage')}`}
          />
        )}
        {filtersData.width > 0 && (
          <ContentItem
            title={t('cargoFields.cargo_specification.width2')}
            value={`${filtersData.width} ${t('units.cubage')}`}
          />
        )}
        {filtersData.height > 0 && (
          <ContentItem
            title={t('cargoFields.cargo_specification.height2')}
            value={`${filtersData.height} ${t('units.cubage')}`}
          />
        )}
        {filtersData.diameter > 0 && (
          <ContentItem
            title={t('cargoFields.cargo_specification.diameter2')}
            value={`${filtersData.diameter} ${t('units.cubage')}`}
          />
        )}
        {filtersData.transportation_type && (
          <ContentItem title={t('cargoFields.cargo_specification.loading')} value={filtersData.transportation_type} />
        )}
        {filtersData.count && (
          <ContentItem title={t('cargoFields.cargo_specification.piece')} value={filtersData.count} />
        )}
        {filtersData.modular_cargo && (
          <ContentItem title={t('cargoFields.cargo_specification.consolidated_cargo_box')} value={t('clearForm.yes')} />
        )}
      </ContentSection>
      <ContentSection title={getTranslate('vehicles_section')} withBorder>
        {(filtersData.bodytypes?.length > 0 || filtersData.bodytype) && (
          <ContentItem
            title={t('cargoFields.transport_section.body')}
            value={
              filtersData.bodytypes?.length > 0
                ? filtersData.bodytypes.map((bt) => `- ${bt.value}`)
                : filtersData.bodytype
            }
          />
        )}
        {filtersData.car_count > 0 && (
          <ContentItem title={t('cargoFields.transport_section.number')} value={filtersData.car_count} />
        )}
        {filtersData.loading_types && filtersData.loading_types.length > 0 && (
          <ContentItem
            title={t('cargoFields.transport_section.loading')}
            value={filtersData.loading_types.map((el) => `- ${el.loading_type}`)}
          />
        )}
        {filtersData.unloading_types && filtersData.unloading_types.length > 0 && (
          <ContentItem
            title={t('cargoFields.transport_section.unloading')}
            value={filtersData.unloading_types.map((el) => `- ${el.unloading_type}`)}
          />
        )}
        {gpsTracking && (
          <ContentItem
            title={t('cargoTracking.titleInGeneralInfo')}
            value={t('cargoTracking.gps')}
          />
        )}
        {(filtersData.cmr || filtersData.t1 || filtersData.tir || filtersData.medical_book) && (
          <ContentItem
            title={t('cargoFields.transport_section.document')}
            value={[
              filtersData.cmr && `- ${t('cargoFields.transport_section.cmr_box')}`,
              filtersData.t1 && `- ${t('cargoFields.transport_section.t1_box')}`,
              filtersData.tir && `- ${t('cargoFields.transport_section.tir_box')}`,
              filtersData.medical_book && `- ${t('cargoFields.transport_section.permit_box')}`,
            ]}
          />
        )}
      </ContentSection>
      {(filtersData || filtersData.payment_rate_type || filtersData.price >= 0) && (
        <ContentSection title={getTranslate('rate_section')} withBorder>
          {filtersData.payment_type && (
            <ContentItem title={t('cargoFields.rate_section.type_rate')} value={filtersData.payment_type} />
          )}
          {filtersData.payment_rate_type && (
            <ContentItem title={t('cargoFields.rate_section.type_payment')} value={filtersData.payment_rate_type} />
          )}
          {filtersData.price >= 0 && (
            <ContentItem
              title={t('cargoFields.rate_section.rate')}
              value={`${getCurrency(filtersData.price, filtersData.prefix)}${
                filtersData.is_vat_included ? ` ${t('cargoFields.rate_section.vat_included')}` : ''
              }`}
            />
          )}
        </ContentSection>
      )}

      {Boolean(
        filtersData.prepayment > 0 ||
          filtersData.bank_days > 0 ||
          filtersData.direct_contract ||
          filtersData.fuel_prepayment ||
          filtersData.upload,
      ) && (
        <ContentSection title={getTranslate('payment_section')} withBorder>
          {filtersData.prepayment > 0 && (
            <ContentItem title={t('cargoFields.payment_section.prepayment')} value={`${filtersData.prepayment}%`} />
          )}
          {filtersData.bank_days > 0 && (
            <ContentItem
              title={t('cargoFields.payment_section.in_grey_text')}
              value={
                <Trans
                  i18nKey="cargoFields.payment_section.in_detail_box"
                  values={{
                    bank_days: filtersData.bank_days,
                  }}
                />
              }
            />
          )}
          {(filtersData.direct_contract || filtersData.fuel_prepayment || filtersData.upload) && (
            <ContentItem
              title={t('cargoFields.payment_section.additional')}
              value={[
                filtersData.direct_contract && `- ${t('cargoFields.payment_section.contract_box')}`,
                filtersData.fuel_prepayment && `- ${t('cargoFields.payment_section.fuel_box')}`,
                filtersData.upload && `- ${t('cargoFields.payment_section.unloading_box')}`,
              ]}
            />
          )}
        </ContentSection>
      )}
      {filtersData.notes && (
        <ContentSection title={t('notes')} withBorder>
          <ContentItem value={filtersData.notes} isFullWidth />
        </ContentSection>
      )}
    </>
  );
}
